import React, { useState } from 'react';
import {
  TableRow, TableCell, RowViewButtons, RowEditButtons,
  DataTableUrlProps, setSorterInUrl, DataTable,
} from 'ui/components/DataTable';
import MultiTypeaheadSelectInput from 'ui/components/MultiTypeaheadSelectInput';
import SelectInput from 'ui/components/TypeaheadSelectInput';
import { spotSelectors, spotOperations } from 'store/spot';
import { skillSelectors } from 'store/skill';
import { employeeSelectors } from 'store/employee';
import { Spot } from 'domain/Spot';
import { TextInput, Text, Chip, ChipGroup, Button, ButtonVariant, FlexItem, Flex, Switch } from '@patternfly/react-core';
import { useDispatch, useSelector } from 'react-redux';
import { Sorter } from 'types';
import { stringSorter } from 'util/CommonSorters';
import { useTranslation } from 'react-i18next';
import * as router from 'react-router-dom';
import { tenantSelectors } from 'store/tenant';
import { useValidators } from 'util/ValidationUtils';
import { getPropsFromUrl } from 'util/BookmarkableUtils';
import { usePageableData } from 'util/FunctionalComponentUtils';
import LoadingSpinner from 'ui/components/LoadingSpinner';
import SpotBlockedEmployeesModal from './SpotBlockedEmployeesModal';
import moment from 'moment';
/* eslint-disable */

export type Props = router.RouteComponentProps;

export const periodicityOptions = [
  { value: 'NO_APLICA', label: 'No aplica' },
  { value: 'CADA_15_DIAS', label: 'Cada 15 días' },
  { value: 'CADA_20_DIAS', label: 'Cada 20 días' },
  { value: 'CADA_MES', label: 'Cada mes' },
  { value: 'CADA_MES_Y_MEDIO', label: 'Cada mes y medio' },
  { value: 'CADA_2_MESES', label: 'Cada 2 meses' },
];

export const assignedDayOptions = [
  { value: null, label: 'No aplica' },
  { value: 'MONDAY', label: 'Lunes' },
  { value: 'TUESDAY', label: 'Martes' },
  { value: 'WEDNESDAY', label: 'Miércoles' },
  { value: 'THURSDAY', label: 'Jueves' },
  { value: 'FRIDAY', label: 'Viernes' },
  { value: 'SATURDAY', label: 'Sábado' },
  { value: 'SUNDAY', label: 'Domingo' },
];

export const midTimeOptions = [
  { value: null, label: 'No aplica' },
  { value: 'AM', label: 'Mañana' },
  { value: 'PM', label: 'Tarde' },
];

export function SpotRow({ spot, setIsLoading } : { spot: Spot; setIsLoading: (loading: boolean) => void }) {
  const [isEditing, setIsEditing] = useState(false);
  const dispatch = useDispatch();
  const tenantId = useSelector(tenantSelectors.getTenantId);
  const history = router.useHistory();

  const [isBlockedModalOpen, setIsBlockedModalOpen] = useState(false);
  const { t } = useTranslation('SpotsPage');

  const periodicityLabels: Record<string, string> = {
    NO_APLICA: 'No aplica',
    CADA_15_DIAS: 'Cada 15 días',
    CADA_20_DIAS: 'Cada 20 días',
    CADA_MES: 'Cada mes',
    CADA_MES_Y_MEDIO: 'Cada mes y medio',
    CADA_2_MESES: 'Cada 2 meses',
  };

  const dayLabels: Record<string, string> = {
    null: 'No aplica',
    MONDAY: 'Lunes',
    TUESDAY: 'Martes',
    WEDNESDAY: 'Miércoles',
    THURSDAY: 'Jueves',
    FRIDAY: 'Viernes',
    SATURDAY: 'Sábado',
    SUNDAY: 'Domingo',
  };

  const midTimeLabels: Record<string, string> = {
    null: 'No aplica',
    AM: 'Mañana',
    PM: 'Tarde',
  };

  if (isEditing) {
    return (<EditableSpotRow spot={spot} isNew={false} onClose={() => setIsEditing(false)} setIsLoading={setIsLoading} />);
  }

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      await dispatch(spotOperations.removeSpot(spot));
      await dispatch(spotOperations.refreshSpotList());
    } catch (error) {
      console.error("Error al eliminar el puesto:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <TableRow>
      <TableCell columnName={t('name')}>
        <Flex>
          <FlexItem>
            <Text>{spot.name}</Text>
          </FlexItem>
        </Flex>
      </TableCell>
      <TableCell columnName={t('isApartment')}>
        <Switch
          isChecked={spot.isApartment}
        />
      </TableCell>
      <TableCell columnName={t('maxService')}>
        <Text>{spot.maxServices}</Text>
      </TableCell>
      <TableCell columnName={t('requiredSkillSet')}>
        <ChipGroup>
          {spot.requiredSkillSet.map((skill) => (
            <Chip key={skill.name} isReadOnly>
              {skill.name}
            </Chip>
          ))}
        </ChipGroup>
      </TableCell>
      <TableCell columnName={t('address')}>
        <Flex>
          <FlexItem>
            <Text>{spot.address}</Text>
          </FlexItem>
        </Flex>
      </TableCell>
      <TableCell columnName={t('city')}>
        <Flex>
          <FlexItem>
            <Text>{spot.city}</Text>
          </FlexItem>
        </Flex>
      </TableCell>
      <TableCell columnName={t('observations')}>
        <Flex>
          <FlexItem>
            <Text>{spot.observations}</Text>
          </FlexItem>
        </Flex>
      </TableCell>
      <TableCell columnName="Programación">
        <div><strong>{periodicityLabels[spot.periodicity] || '-'}</strong></div>
        {spot.periodicity !== 'NO_APLICA' && (
          <>
            <div>Día Asignado: {dayLabels[spot.diaAsignado || ''] || '-'}</div>
            <div>Empleado Rotación: {
                spot.rotationEmployee ?
                `${spot.rotationEmployee.name}` : '-'
              }
            </div>
            {spot.horarioMedioTiempoPreferido !== null && (
              <div>
                <div><b>Medio Tiempo</b></div>
                <div>Franja Horaria: {midTimeLabels[spot.horarioMedioTiempoPreferido || ''] || '-'}</div>
                <div>Cant. Horas : {spot.cantHorasMedioTiempo
                      ? moment(spot.cantHorasMedioTiempo, 'HH:mm').format('HH:mm')
                      : '-'}</div>
                <div>Hora Inicio: {spot.inicioMedioTiempo
                      ? moment(spot.inicioMedioTiempo, 'HH:mm').format('HH:mm')
                      : '-'}</div>
              </div>
            )}
          </>
        )}
      </TableCell>
      <TableCell columnName={t('status')}>
        <Switch
          isChecked={spot.status}
        />
      </TableCell>
      <>
        <TableCell columnName="Acciones">
          <Button
            variant={ButtonVariant.secondary}
            onClick={() => setIsBlockedModalOpen(true)}
            style={{ marginRight: '8px' }}
          >
            Bloq. ({spot.blockedEmployeeSet?.length || 0})
          </Button>
        </TableCell>

        <RowViewButtons
          onEdit={() => setIsEditing(true)}
          onDelete={handleDelete}
        />

        <SpotBlockedEmployeesModal
          spot={spot}
          isOpen={isBlockedModalOpen}
          onClose={() => setIsBlockedModalOpen(false)}
        />
      </>
    </TableRow>
  );
}

export function EditableSpotRow(props: { spot: Spot; isNew: boolean; onClose: () => void; setIsLoading: (loading: boolean) => void; }) {
  const [name, setName] = useState(props.spot.name);
  const [address, setAddress] = useState(props.spot.address);
  const [city, setCity] = useState(props.spot.city);
  const [maxServices, setMaxService] = useState(props.spot.maxServices); 
  const [observations, setObservations] = useState(props.spot.observations);
  const [periodicity, setPeriodicity] = useState(props.spot.periodicity);
  const [assignedDay, setAssignedDay] = useState(props.spot.diaAsignado);
  const [midTime, setMidTime] = useState(props.spot.horarioMedioTiempoPreferido);
  const [requiredSkillSet, setRequiredSkillSet] = useState(props.spot.requiredSkillSet);
  const [cantHoras, setCantHoras] = useState(props.spot.cantHorasMedioTiempo);
  const [startTime, setStartTime] = useState(props.spot.inicioMedioTiempo);
  const [rotationEmployee, setRotationEmployee] = useState(props.spot.rotationEmployee || null)
  const spotList = useSelector(spotSelectors.getSpotList);
  const employeeList = useSelector(employeeSelectors.getActiveEmployeeList);
  const skillList = useSelector(skillSelectors.getSkillList);
  const [isApartment, setIsApartment] = useState(props.spot.isApartment);
  const [status, setStatus] = useState(props.spot.status);
  const dispatch = useDispatch();
  const { t } = useTranslation('SpotsPage');

  const validators = {
    nameMustNotBeEmpty: {
      predicate: (spot: Spot) => spot.name.length > 0,
      errorMsg: () => t('spotEmptyNameError'),
    },
    nameAlreadyTaken: {
      predicate: (spot: Spot) => spotList.filter((otherSpot) => otherSpot.name === spot.name
        && otherSpot.id !== spot.id).length === 0,
      errorMsg: (spot: Spot) => t('spotNameAlreadyTakenError', { name: spot.name }),
    },
    missingCantHorasOrStartTimeWhenMidTime: {
      predicate: (spot: Spot) => (spot.horarioMedioTiempoPreferido === null) ||
        (spot.cantHorasMedioTiempo !== null && spot.inicioMedioTiempo !== null),
      errorMsg: () => 'Hora de inicio y cantidad de horas es obligatorio',
    },
    invalidStartTimeForMidTime: {
      predicate: (spot: Spot) => {
        if (!spot.inicioMedioTiempo || !spot.horarioMedioTiempoPreferido) return true;

        const [hour] = spot.inicioMedioTiempo.split(':').map(Number);
        if (spot.horarioMedioTiempoPreferido === 'AM') {
          return hour >= 0 && hour < 12;
        } else if (spot.horarioMedioTiempoPreferido === 'PM') {
          return hour >= 12 && hour < 24;
        }
        return true; // NO_APLICA u otro valor
      },
      errorMsg: () => 'La hora de inicio no corresponde con el horario AM/PM seleccionado',
    }
  };

  const validationErrors = useValidators({
    ...props.spot,
    name,
    requiredSkillSet,
    address,
    city,
    observations,
    periodicity,
    horarioMedioTiempoPreferido: midTime,
    cantHorasMedioTiempo: cantHoras,
    inicioMedioTiempo: startTime,
    isApartment,
    status,
    maxServices,
  }, validators);

  const handleSave = async () => {
    props.setIsLoading(true);
    
    const finalAssignedDay = (periodicity === 'NO_APLICA') ? null : assignedDay;
    const finalMidTime = (periodicity === 'NO_APLICA') ? null : midTime;
    const isValidTime = (value: string | null) => value !== null && moment(value, 'HH:mm', true).isValid();
    const finalCantHoras = (periodicity === 'NO_APLICA' || midTime === null || !isValidTime(cantHoras))
      ? null
      : moment(cantHoras, 'HH:mm').format('HH:mm');

    const finalStartTime = (periodicity === 'NO_APLICA' || midTime === null || !isValidTime(startTime))
      ? null
      : moment(startTime, 'HH:mm').format('HH:mm');

    const finalRotationEmployeeid = periodicity === 'NO_APLICA' ? null : rotationEmployee?.id || null;

    const spotPayload = {
      ...props.spot,
      name,
      requiredSkillSet,
      address,
      city,
      observations,
      periodicity,
      diaAsignado: finalAssignedDay,
      horarioMedioTiempoPreferido: finalMidTime,
      cantHorasMedioTiempo: finalCantHoras,
      inicioMedioTiempo: finalStartTime,
      rotationEmployeeId: finalRotationEmployeeid,
      isApartment: isApartment,
      status: status,
      maxServices: maxServices,
    };

    try {
      if (props.isNew) {
        await dispatch(spotOperations.addSpot(spotPayload));
      } else {
        await dispatch(spotOperations.updateSpot(spotPayload));
      }
      await dispatch(spotOperations.refreshSpotList());
    } catch (error) {
      console.error('Error al guardar el puesto:', error);
    } finally {
      props.setIsLoading(false);
    }
  };

  return (
    <TableRow>
      <TableCell columnName={t('name')}>
        <TextInput value={name} onChange={setName} />
        {validationErrors.showValidationErrors('nameMustNotBeEmpty', 'nameAlreadyTaken')}
      </TableCell>
      <TableCell columnName={t('isApartment')}>
        <Switch
          isChecked={isApartment}
          onChange={(checked) => setIsApartment(checked)}
        />
      </TableCell>
      <TableCell columnName={t('maxService')}>
        <TextInput
          type="number"
          min={1}
          value={maxServices ?? ''}
          onChange={(value) =>
            setMaxService(value === '' ? null : Number(value))
          }
        />
      </TableCell>
      <TableCell columnName={t('requiredSkillSet')}>
        <MultiTypeaheadSelectInput
          value={requiredSkillSet}
          options={skillList}
          optionToStringMap={(skill) => skill.name}
          onChange={setRequiredSkillSet}
          emptyText={t('selectRequiredSkills')}
        />
      </TableCell>
      <TableCell columnName={t('address')}>
        <TextInput value={address} onChange={setAddress} />
      </TableCell>
      <TableCell columnName={t('city')}>
        <TextInput value={city} onChange={setCity} />
      </TableCell>
      <TableCell columnName={t('observations')}>
        <TextInput value={observations} onChange={setObservations} />
      </TableCell>
      <TableCell columnName={t('periodicity')}>
          <div style={{fontSize: '13px', color: 'gray'}}>Periodicidad</div>
          <SelectInput
            value={periodicityOptions.find(option => option.value === periodicity)}
            options={periodicityOptions}
            onChange={(option) => setPeriodicity((option?.value || 'NO_APLICA') as Spot['periodicity'])}
            optionToStringMap={(option) => option.label}
            emptyText={t('selectField')}
            autoSize={false}
          />
        {periodicity !== 'NO_APLICA' && (
          <div style={{marginTop: '10px'}}>
            <div style={{fontSize: '13px', color: 'gray'}}>Día asignado</div>
            <SelectInput
              value={assignedDayOptions.find((option) => option.value === (assignedDay ?? ''))}
              options={assignedDayOptions}
              onChange={(option) => setAssignedDay((option?.value || '') as Spot['diaAsignado'])}
              optionToStringMap={(option) => option.label}
              emptyText={t('selectField')}
              autoSize={false}
            />
          </div>
        )}
        {periodicity !== 'NO_APLICA' && (
          <div style={{marginTop: '10px'}}>
            <div style={{fontSize: '13px', color: 'gray'}}>Empleado asignado</div>
            <SelectInput
              value={rotationEmployee
                ? { value: rotationEmployee.id, label: rotationEmployee.name }
                : { value: null, label: 'No aplica' }
              }
              options={[
                { value: null, label: 'No aplica' },
                ...employeeList.map(e => ({ value: e.id, label: e.name }))
              ]}
              onChange={(option) => {
                if (option?.value === null) {
                  setRotationEmployee(null);
                } else {
                  const selectedEmployee = employeeList.find(e => e.id === option?.value);
                  setRotationEmployee(selectedEmployee || null);
                }
              }}
              optionToStringMap={(option) => option? option.label : ''}
              emptyText='Seleccione un empleado'
              autoSize={false}
            />
          </div>
        )}
        {periodicity !== 'NO_APLICA' && (
          <div style={{marginTop: '10px'}}>
            <div style={{fontSize: '13px', color: 'gray'}}>Solo para medio tiempo</div>
            <SelectInput
              value={midTimeOptions.find((option) => option.value === (midTime ?? ''))}
              options={midTimeOptions}
              onChange={(option) => setMidTime((option?.value || null) as Spot['horarioMedioTiempoPreferido'])}
              optionToStringMap={(option) => option.label}
              emptyText={t('selectField')}
              autoSize={false}
            />
          </div>
        )}
        {(midTime !== null && periodicity !== 'NO_APLICA') && (
          <div style={{marginTop: '10px'}}>
            <div style={{fontSize: '13px', color: 'gray'}}>Cant. horas</div>
            {(() => {
              const horas = cantHoras ? parseInt(cantHoras.split(":")[0]) : 0;
              const minutos = cantHoras ? parseInt(cantHoras.split(":")[1]) : 0;

              return (
                <div style={{ display: 'flex', gap: '10px' }}>
                  <div>
                    <div style={{ fontSize: '13px', color: 'gray' }}>Horas</div>
                    <SelectInput
                      value={{ value: horas, label: `${horas} h` }}
                      options={[0, 1, 2, 3, 4].map(h => ({ value: h, label: `${h} h` }))}
                      onChange={(option) => {
                        const h = option?.value || 0;
                        const m = minutos;
                        const nuevaHora = `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
                        setCantHoras(nuevaHora);
                      }}
                      optionToStringMap={(option) => option.label}
                      emptyText={t('selectField')}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: '13px', color: 'gray' }}>Minutos</div>
                    <SelectInput
                      value={{ value: minutos, label: `${minutos} min` }}
                      options={[0, 15, 30, 45].map(m => ({ value: m, label: `${m} min` }))}
                      onChange={(option) => {
                        const m = option?.value || 0;
                        const h = horas;
                        const nuevaHora = `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
                        setCantHoras(nuevaHora);
                      }}
                      optionToStringMap={(option) => option.label}
                      emptyText={t('selectField')}
                    />
                  </div>
                  {validationErrors.showValidationErrors('missingCantHorasOrStartTimeWhenMidTime')}
                </div>
              );
            })()}
          </div>
        )}
        {(midTime !== null && periodicity !== 'NO_APLICA') && (
          <div style={{marginTop: '10px'}}>
            <div style={{fontSize: '13px', color: 'gray'}}>Hora inicio</div>
            <TextInput
              aria-label="Start Time"
              type="time"
              value={startTime ?? ''}
              onChange={(val) => setStartTime(val)}
            />
             {validationErrors.showValidationErrors('invalidStartTimeForMidTime')}
          </div>
        )}
      </TableCell>
      <TableCell columnName={t('status')}>
        <Switch
          isChecked={status}
          onChange={(checked) => setStatus(checked)}
        />
      </TableCell>
      <RowEditButtons
        isValid={validationErrors.isValid}
        onSave={handleSave}
        onClose={() => props.onClose()}
      />
    </TableRow>
  );
}

export const SpotsPage: React.FC<Props> = (props) => {
  const spotList = useSelector(spotSelectors.getSpotList);
  const tenantId = useSelector(tenantSelectors.getTenantId);
  const [isLoading, setIsLoading] = useState(false);

  const { t } = useTranslation('SpotsPage');

  const columns = [
    { name: t('name'), sorter: stringSorter<Spot>((spot) => spot.name), width: '15%' },
    { name: t('isApartment'), width: '10%' },
    { name: t('maxService'), width: '10%' },
    { name: t('requiredSkillSet'), width: '15%' },
    { name: t('address'), width: '10%' },
    { name: t('city'), width: '10%' },
    { name: t('observations'), width: '15%' },
    { name: t('periodicityTittle'), width: '15%' },
    { name: t('status'), with: '10%' },
  ];

  const urlProps = getPropsFromUrl<DataTableUrlProps>(props, {
    page: '1',
    itemsPerPage: '10',
    filter: null,
    sortBy: '0',
    asc: 'true',
  });

  const sortBy = parseInt(urlProps.sortBy || '-1', 10);
  const sorter = columns[sortBy].sorter as Sorter<Spot>;

  const pageableData = usePageableData(urlProps, spotList, (spot) => [spot.name,
    ...spot.requiredSkillSet.map((skill) => skill.name)], sorter);

  return (
    <>
      <LoadingSpinner isLoading={isLoading} />
      <DataTable
        {...props}
        {...pageableData}
        title={t('spots')}
        columns={columns}
        rowWrapper={(spot) => (<SpotRow key={spot.id} spot={spot} setIsLoading={setIsLoading} />)}
        sortByIndex={sortBy}
        onSorterChange={(index) => setSorterInUrl(props, urlProps, sortBy, index)}
        newRowWrapper={(removeRow) => (
          <EditableSpotRow
            isNew
            onClose={removeRow}
            spot={{
              tenantId,
              name: '',
              requiredSkillSet: [],
              blockedEmployeeSet: [],
              address: '',
              city: '',
              observations: '',
              periodicity: 'NO_APLICA',
              diaAsignado: null,
              horarioMedioTiempoPreferido: null,
              fechaUltimoTurnoPublicado: '',
              cantHorasMedioTiempo: null,
              inicioMedioTiempo: null,
              rotationEmployee: null,
              isApartment: false,
              status: true,
              maxServices: null,
            }}
            setIsLoading={setIsLoading}
          />
        )}
      />
    </>
  );
};

export default SpotsPage;
